import { Dropdown } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { QuizSetContributorResRole } from '@/api/gen/_model/quizSetContributorResRole';
import { AuthorTagChip, useOpenAuthorProfile } from '@/components/AuthorTagChip';
import { ChevronDownIcon, SnailIcon } from '@/icons';
import { ZepQuizSmallLogoIcon } from '@/icons/components/ZepQuizSmallLogo';
import { cn } from '@/utils/tailwind.util';
import { type HeroCardOriginalAuthor } from '../../types';
type OriginalAuthorProps = {
  authors: HeroCardOriginalAuthor[];
  className?: string;
};

/**
 * 원작자 표기. 한 명이면 칩 하나, 여럿이면 `첫 사람 외 n명` 요약 + 드롭다운 목록이다.
 *
 * 여럿일 때의 문구는 `common` 키다 — `quizDetail.originAuthor.more`(요약 칩) ·
 * `quizDetail.originAuthor.quizSetOrigin`(그룹 머리글). 원작자 그룹 머리글은 한 명일 때
 * 쓰는 `quizDetail.originQuizAuthor` 를 그대로 재사용한다 — 같은 뜻이라 키를 새로 만들지 않았다.
 */
export const OriginalAuthor = ({
  authors,
  className
}: OriginalAuthorProps) => {
  const {
    t
  } = useTranslation(['common']);
  const [first, ...rest] = authors;
  if (first == null) return null;
  const label = <div className="flex items-center gap-3xs text-alternative">
      <SnailIcon size="xs" className="hidden tablet-sm:block" />
      <SnailIcon size="2xs" className="tablet-sm:hidden" />
      <span className="text-caption-lg-medium tablet-sm:text-body-sm-medium">
        {t('quizDetail.originQuizAuthor')}
      </span>
      <span>-</span>
    </div>;
  if (rest.length === 0) {
    return <div className={cn('flex items-center gap-3xs', className)}>
        {label}
        <AuthorTagChip authorName={first.name} isOfficial={first.isAmbassador} authorHashId={first.id} />
      </div>;
  }
  return <div className={cn('flex items-center gap-3xs', className)} data-sentry-component="OriginalAuthor" data-sentry-source-file="OriginalAuthor.tsx">
      {label}
      <Dropdown.Root data-sentry-element="unknown" data-sentry-source-file="OriginalAuthor.tsx">
        {/* 요약 칩은 프로필로 가지 않는다 — 펼친 목록의 각 항목이 이동을 맡는다 */}
        <Dropdown.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="OriginalAuthor.tsx">
          <button type="button" className="group/authors inline-flex w-fit shrink-0 items-center gap-4xs rounded-xl border-[1px] border-alternative px-xs py-3xs text-body-sm-semibold text-neutral hover:text-assistive focus-visible:focus-ring">
            <span className="truncate">{first.name}</span>
            {/* 엠버서더 로고는 첫 사람의 표식이라 이름 바로 뒤에 둔다. 트리거라 툴팁은 없다 */}
            {first.isAmbassador && <ZepQuizSmallLogoIcon size="2xs" className="shrink-0" />}
            <span className="shrink-0">
              {t('common:quizDetail.originAuthor.more', {
              n: rest.length
            })}
            </span>
            <ChevronDownIcon size="2xs" className="shrink-0 text-alternative transition-transform group-data-[state=open]/authors:rotate-180" data-sentry-element="ChevronDownIcon" data-sentry-source-file="OriginalAuthor.tsx" />
          </button>
        </Dropdown.Trigger>

        {/* 히어로 카드가 overflow 를 자르므로 Portal 로 띄우고, 트리거 폭 고정을 푼다 */}
        <Dropdown.Portal data-sentry-element="unknown" data-sentry-source-file="OriginalAuthor.tsx">
          <Dropdown.Content align="start" className="max-h-[280px] w-auto min-w-[200px] max-w-[280px] overflow-y-auto" data-sentry-element="unknown" data-sentry-source-file="OriginalAuthor.tsx">
            <AuthorGroup title={t('common:quizDetail.originAuthor.quizSetOrigin')} authors={authors.filter(author => author.role === QuizSetContributorResRole.QUIZSET_ORIGIN)} data-sentry-element="AuthorGroup" data-sentry-source-file="OriginalAuthor.tsx" />
            <AuthorGroup title={t('common:quizDetail.originQuizAuthor')} authors={authors.filter(author => author.role === QuizSetContributorResRole.QUIZ_BANK)} data-sentry-element="AuthorGroup" data-sentry-source-file="OriginalAuthor.tsx" />
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>;
};

/** 출처별 그룹. 비어 있으면 그룹 자체를 그리지 않는다. */
const AuthorGroup = ({
  title,
  authors
}: {
  title: string;
  authors: HeroCardOriginalAuthor[];
}) => {
  const {
    canOpen,
    open
  } = useOpenAuthorProfile();
  if (authors.length === 0) return null;
  return <div className="flex flex-col" data-sentry-component="AuthorGroup" data-sentry-source-file="OriginalAuthor.tsx">
      <span className="px-2xs py-3xs text-caption-lg-medium text-alternative">
        {title}
      </span>
      {authors.map(author => <Dropdown.Item key={author.id} disabled={!canOpen} onSelect={() => open(author.id)}>
          <span className="truncate">{author.name}</span>
          {author.isAmbassador && <ZepQuizSmallLogoIcon size="2xs" className="shrink-0" />}
        </Dropdown.Item>)}
    </div>;
};